import React from 'react';
import { Award, TrendingUp, Users, Target, Briefcase, ArrowRight } from 'lucide-react';

const achievements = [
  {
    icon: Award,
    title: '15 Years',
    subtitle: 'Enterprise Experience',
    description: 'Across Fintech, Payments & Healthcare — we have worked in the most demanding digital environments.',
  },
  {
    icon: TrendingUp,
    title: '$8.7M',
    subtitle: 'Problem Identified',
    description: 'Calculated the real cost of fragmented tooling and unclear metrics in a 50-person engineering organisation.',
  },
  {
    icon: Users,
    title: '1,000+',
    subtitle: 'Engineering Hours Saved',
    description: 'Across clients — by eliminating manual reporting, streamlining pipelines, and automating quality gates.',
  },
  {
    icon: Target,
    title: 'Mission First',
    subtitle: 'Built From Frustration',
    description: 'We build tools and processes we actually needed ourselves. Every solution stems from real-world pain.',
  },
];

const About = () => {
  return (
    <section id="about" className="relative py-24 overflow-hidden">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] blur-3xl pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(0,212,255,0.05) 0%, transparent 70%)' }} />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] blur-3xl pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(0,102,255,0.05) 0%, transparent 70%)' }} />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20 items-center">
          <div>
            <div className="inline-flex items-center space-x-2 rounded-full px-4 py-1.5 mb-6"
              style={{ background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.25)' }}>
              <Briefcase className="w-3.5 h-3.5 text-cyan-400" />
              <span className="text-xs font-medium text-cyan-400 tracking-wide uppercase">Who We Are</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Engineers who've<br />
              <span className="gradient-text">felt the pain</span>
            </h2>
            <p className="text-gray-400 leading-relaxed mb-4">
              IronGate was founded by a Quality Engineer who spent 15 years in enterprise environments across Payments, 
              FinTech, and Healthcare — watching companies struggle with the same problems over and over: fragmented tools, 
              unclear metrics, and a gap between what engineering teams know and what leadership understands.
            </p>
            <p className="text-gray-400 leading-relaxed mb-8">
              We don't just advise. We build. Whether that's transforming your infrastructure, implementing AI, 
              writing automation frameworks, or fixing the broken relationship between quality and the business — 
              we do it hands-on, alongside your team.
            </p>
            <a
              href="#contact"
              onClick={e => { e.preventDefault(); document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="inline-flex items-center space-x-2 text-sm font-semibold text-cyan-400 hover:text-cyan-300 transition-colors"
            >
              <span>Let's work together</span>
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          {/* Story callout */}
          <div className="rounded-2xl p-8 relative overflow-hidden"
            style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.07)',
            }}>
            <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-cyan-500 to-blue-600" />
            <h3 className="text-lg font-bold text-white mb-5">The Problem We Kept Seeing</h3>
            <div className="space-y-4">
              {[
                {
                  side: 'Engineering',
                  text: 'Drowning in data across Jenkins, Jira, SonarQube, TestRail — but unable to tell a clear story to leadership.',
                  color: '#00d4ff',
                },
                {
                  side: 'Leadership',
                  text: 'Making million-dollar decisions on gut feel because the metrics didn\'t speak their language.',
                  color: '#a855f7',
                },
                {
                  side: 'The Gap',
                  text: 'No connective tissue between technical quality and business outcomes — so neither side trusted the other.',
                  color: '#f59e0b',
                },
              ].map((item, i) => (
                <div key={i} className="flex items-start space-x-3">
                  <div className="mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: item.color }} />
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wide" style={{ color: item.color }}>{item.side}: </span>
                    <span className="text-sm text-gray-400">{item.text}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-5 border-t border-white/5">
              <p className="text-sm text-gray-400 italic">
                "We built QE Navigator because we desperately needed it. Now it's available to everyone."
              </p>
              <p className="text-xs text-gray-600 mt-2">— Ionut Apostu, Founder, IronGate Software</p>
            </div>
          </div>
        </div>

        {/* Achievements */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {achievements.map((item, i) => {
            const Icon = item.icon;
            return (
              <div
                key={i}
                className="rounded-2xl p-6 text-center hover:-translate-y-1 transition-transform duration-300"
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.07)',
                }}
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-4"
                  style={{ background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.2)' }}>
                  <Icon className="w-6 h-6 text-cyan-400" />
                </div>
                <div className="text-2xl font-bold gradient-text mb-1">{item.title}</div>
                <div className="text-xs font-semibold text-gray-300 mb-2 uppercase tracking-wide">{item.subtitle}</div>
                <p className="text-xs text-gray-500 leading-relaxed">{item.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default About;
